import { motion } from 'framer-motion';
import { Camera, ChevronRight, MapPinOff, MessageSquareWarning } from 'lucide-react';
import { cn } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { WriteOffStatusBadge } from './WriteOffStatusBadge.js';
import {
  STATUS_STRIPE,
  dayOf,
  enterFrom,
  enterTransition,
  qtyText,
  unitOf,
  type WriteOffRowsViewProps,
} from './write-off-row.js';

const th = 'px-4 py-3 text-left text-[0.6875rem] font-semibold tracking-wide text-text-muted uppercase';
const td = 'px-4 py-3 align-middle';

/**
 * KENG EKRAN ro'yxati (lg+) — zich, lekin o'qiladigan jadval.
 *
 * Umumiy `Table` ishlatilmadi: uning yopishqoq sarlavhasi faqat cheklangan
 * bo'yli O'RAM ichida ishlaydi (izohi `Table.tsx` da). Bu yerda sarlavha
 * SAHIFA bo'yicha, topbar ostiga yopishishi kerak — shuning uchun o'ram
 * `overflow-clip` (aylantiruvchi quti YARATMAYDI) va `thead` `top` ni
 * `--size-topbar` dan oladi.
 *
 * Qator sichqoncha bilan bosiladi; klaviatura uchun oxirgi ustunda haqiqiy
 * tugma bor.
 */
export const WriteOffsTable = ({ rows, onOpen, reduceMotion }: WriteOffRowsViewProps) => (
  <div className="overflow-clip rounded-2xl border border-border bg-surface shadow-[var(--shadow-1)]">
    <table className="w-full border-separate border-spacing-0 text-sm">
      <caption className="sr-only">{t('writeOff.title')}</caption>
      <thead className="sticky top-[var(--size-topbar)] z-[var(--z-sticky)]">
        <tr className="bg-surface-2/95 backdrop-blur-sm">
          <th scope="col" className={cn(th, 'border-b border-border pl-5')}>
            {t('writeOff.docNo')}
          </th>
          <th scope="col" className={cn(th, 'border-b border-border')}>
            {t('writeOff.product')}
          </th>
          <th scope="col" className={cn(th, 'border-b border-border text-right')}>
            {t('writeOff.qty')}
          </th>
          <th scope="col" className={cn(th, 'border-b border-border')}>
            {t('writeOff.subdivision')}
          </th>
          <th scope="col" className={cn(th, 'border-b border-border')}>
            {t('writeOff.submittedAt')}
          </th>
          <th scope="col" className={cn(th, 'border-b border-border')}>
            {t('writeOff.statusColumn')}
          </th>
          <th scope="col" className={cn(th, 'border-b border-border')}>
            <span className="sr-only">{t('writeOff.details')}</span>
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, index) => {
          const when = dayOf(row.submittedAt);
          const last = index === rows.length - 1;
          const cell = cn(td, last ? undefined : 'border-b border-border');
          return (
            <motion.tr
              key={row.id}
              initial={enterFrom(reduceMotion)}
              animate={{ opacity: 1, y: 0 }}
              transition={enterTransition(index, reduceMotion)}
              onClick={() => onOpen(row.id)}
              className={cn(
                'group cursor-pointer transition-colors duration-[var(--motion-fast)]',
                'hover:bg-hover',
              )}
            >
              <td className={cn(cell, 'relative pl-5')}>
                <span
                  aria-hidden
                  className={cn(
                    'absolute inset-y-2 left-0 w-1 rounded-r-full opacity-70',
                    STATUS_STRIPE[row.status],
                  )}
                />
                <span className="tabular font-mono text-[0.8125rem] font-semibold whitespace-nowrap text-text">
                  {row.docNo}
                </span>
              </td>

              <td className={cn(cell, 'max-w-[22rem]')}>
                <div className="flex min-w-0 flex-col gap-0.5">
                  <span className="truncate font-medium text-text">{row.nomenclatureName}</span>
                  <span className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-text-muted">
                    <span className="tabular font-mono">{row.batchNo}</span>
                    <span className="inline-flex items-center gap-1">
                      <Camera size={12} aria-hidden />
                      <span className="tabular">{row.photoCount}</span>
                      <span className="sr-only">{t('writeOff.photos')}</span>
                    </span>
                    {row.locationLat === null ? (
                      <span className="inline-flex items-center gap-1 font-medium text-warn">
                        <MapPinOff size={12} aria-hidden />
                        {t('writeOff.noLocation')}
                      </span>
                    ) : null}
                    {row.lastRejectReason === null ? null : (
                      <span
                        className="inline-flex max-w-full min-w-0 items-center gap-1 text-danger"
                        title={row.lastRejectReason}
                      >
                        <MessageSquareWarning size={12} aria-hidden className="shrink-0" />
                        <span className="truncate">{row.lastRejectReason}</span>
                      </span>
                    )}
                  </span>
                </div>
              </td>

              <td className={cn(cell, 'text-right whitespace-nowrap')}>
                <span className="tabular font-semibold text-text">{qtyText(row.qty)}</span>{' '}
                <span className="text-xs text-text-muted">{unitOf(row)}</span>
              </td>

              <td className={cell}>
                <div className="flex min-w-0 flex-col gap-0.5">
                  <span className="truncate text-text">{row.subdivisionName || '—'}</span>
                  <span className="truncate text-xs text-text-muted">{row.createdByName ?? '—'}</span>
                </div>
              </td>

              <td className={cn(cell, 'whitespace-nowrap')}>
                <time dateTime={row.submittedAt} className="tabular flex flex-col gap-0.5">
                  <span className="text-text">{when.date}</span>
                  <span className="text-xs text-text-muted">{when.time}</span>
                </time>
              </td>

              <td className={cell}>
                <WriteOffStatusBadge status={row.status} />
              </td>

              <td className={cn(cell, 'w-px pr-3 text-right')}>
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    onOpen(row.id);
                  }}
                  aria-label={`${t('writeOff.details')}: ${row.docNo}`}
                  className={cn(
                    'inline-flex size-9 items-center justify-center rounded-xl text-text-muted',
                    'transition-colors duration-[var(--motion-fast)] outline-none',
                    'group-hover:bg-accent-soft group-hover:text-accent',
                    'focus-visible:ring-2 focus-visible:ring-accent',
                  )}
                >
                  <ChevronRight size={18} aria-hidden />
                </button>
              </td>
            </motion.tr>
          );
        })}
      </tbody>
    </table>
  </div>
);
